import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import {
  Group as GroupIcon,
  CheckCircle as CheckCircleIcon,
  Diamond as DiamondIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import type { SubscriptionSummary } from '../types/subscription.types';
import { formatNaira } from '../../../services/fleetSubscriptionService';

interface SubscriptionStatsCardsProps {
  summary?: SubscriptionSummary;
  isLoading?: boolean;
}

interface StatCard {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}

const SubscriptionStatsCards: React.FC<SubscriptionStatsCardsProps> = ({
  summary,
  isLoading = false,
}) => {
  if (isLoading || !summary) {
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Skeleton variant="rectangular" height={100} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  const stats: StatCard[] = [
    {
      title: 'Total Hosts',
      value: summary.total_hosts,
      subtitle: `${summary.fleet_managers} Fleet • ${summary.individuals} Individual`,
      icon: <GroupIcon sx={{ fontSize: 40 }} />,
      color: '#3b82f6',
      bgColor: 'rgba(59, 130, 246, 0.1)',
    },
    {
      title: 'Active Subscriptions',
      value: summary.active_subscriptions,
      subtitle: summary.expired_subscriptions
        ? `${summary.expired_subscriptions} expired`
        : 'No expired subscriptions',
      icon: <CheckCircleIcon sx={{ fontSize: 40 }} />,
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
    },
    {
      title: 'Premium Qualified',
      value: summary.qualified_by_premium + summary.qualified_by_both,
      subtitle: `${summary.qualified_by_premium} premium only • ${summary.qualified_by_both} both`,
      icon: <DiamondIcon sx={{ fontSize: 40 }} />,
      color: '#9333ea',
      bgColor: 'rgba(147, 51, 234, 0.1)',
    },
    {
      title: 'Total Revenue',
      value: formatNaira(summary.total_revenue || 0),
      subtitle: `From ${summary.active_subscriptions} subscriptions`,
      icon: <MoneyIcon sx={{ fontSize: 40 }} />,
      color: '#f59e0b',
      bgColor: 'rgba(245, 158, 11, 0.1)',
    },
  ];

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {stats.map((stat, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Card
            sx={{
              height: '100%',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 8px 24px rgba(0,0,0,0.4)'
                    : '0 8px 24px rgba(0,0,0,0.1)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 500 }}
                  >
                    {stat.title}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>
                    {stat.value}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    backgroundColor: stat.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: stat.color,
                  }}
                >
                  {stat.icon}
                </Box>
              </Box>
              {stat.subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {stat.subtitle}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Additional Info Cards */}
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                backgroundColor: 'rgba(59, 130, 246, 0.05)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Qualified by Vehicles
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                      {summary.qualified_by_vehicles + summary.qualified_by_both}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                backgroundColor: 'rgba(147, 51, 234, 0.05)',
                border: '1px solid rgba(147, 51, 234, 0.2)',
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DiamondIcon sx={{ color: '#9333ea', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Premium Only
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#9333ea' }}>
                      {summary.qualified_by_premium}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Not Qualified
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#ef4444' }}>
                      {summary.not_qualified}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SubscriptionStatsCards;
